import * as React from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"
import { cn } from "@/lib/utils"

// Tooltip styles
const tooltipStyles = {
  trigger: {
    position: 'relative' as const,
    display: 'inline-block',
    width: '100%',
    cursor: 'default'
  }
};

interface SidebarTooltipProps extends React.HTMLAttributes<HTMLSpanElement> {
  content?: string | React.ReactNode
  className?: string
  children?: React.ReactNode
  clickable?: boolean // If true, do not stopPropagation on click
}

export function SidebarTooltip({ 
  content, 
  clickable,
  className, 
  children, 
  ...props 
}: SidebarTooltipProps) {
  const componentId = React.useId();
  // console.log(`[SidebarTooltip:${componentId}] Rendering with:`, { 
  //   hasContent: !!content, 
  //   hasChildren: !!children,
  //   className 
  // });
  // State
  const [isEllipsisActive, setIsEllipsisActive] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  
  // Refs
  const textRef = React.useRef<HTMLSpanElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // Check if text overflows and has ellipsis
  React.useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkEllipsis = () => {
      const isActive = element.offsetWidth < element.scrollWidth ||
                     element.offsetHeight < element.scrollHeight;
      setIsEllipsisActive(isActive);
    };
    
    // Initial check
    const timeoutId = setTimeout(checkEllipsis, 100);
    
    // Cleanup
    return () => clearTimeout(timeoutId);
  }, [content, children]);

  // Determine if we should show the tooltip
  const shouldShowTooltip = isEllipsisActive && !!content && !!children;
  
  // Handle hover state
  const handleHover = (isHovering: boolean) => {
    setIsHovered(isHovering);
    if (shouldShowTooltip) {
      setIsOpen(isHovering);
    }
  };

  // console.log(`[SidebarTooltip:${componentId}] Rendering with tooltip`);
  
  const handleMouseEnter = (e: React.MouseEvent) => {
    // console.log(`[SidebarTooltip:${componentId}] Mouse Enter`, {
    //   target: e.currentTarget,
    //   isEllipsisActive,
    //   hasContent: !!content,
    //   hasChildren: !!children,
    //   rect: e.currentTarget.getBoundingClientRect(),
    //   styles: window.getComputedStyle(e.currentTarget)
    // });
    
    // Force trigger the tooltip programmatically if needed
    const tooltipTrigger = e.currentTarget.closest('[data-state]');
    // console.log(`[SidebarTooltip:${componentId}] Tooltip Trigger Element:`, tooltipTrigger);
    
    // Check for any parent elements that might be interfering with events
    let current = e.currentTarget.parentElement;
    while (current) {
      const styles = window.getComputedStyle(current);
      // console.log(`[SidebarTooltip:${componentId}] Parent Element Check:`, {
      //   tag: current.tagName,
      //   class: current.className,
      //   pointerEvents: styles.pointerEvents,
      //   position: styles.position,
      //   zIndex: styles.zIndex,
      //   overflow: styles.overflow
      // });
      current = current.parentElement;
    }
  };

  const handleTooltipOpenChange = (open: boolean) => {
    // console.log(`[SidebarTooltip:${componentId}] Tooltip ${open ? 'opened' : 'closed'}`);
  };

  return (
    <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
      {shouldShowTooltip ? (
        <Tooltip open={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger asChild>
            <span 
              ref={textRef}
              className={cn("truncate w-full inline-block", className)}
              style={tooltipStyles.trigger}
              onMouseEnter={() => handleHover(true)}
              onMouseLeave={() => handleHover(false)}
              onClick={clickable ? undefined : (e => {
                e.stopPropagation();
                setIsOpen(prev => !prev);
              })}
              {...props}
            >
              {children}
            </span>
          </TooltipTrigger>
          <TooltipContent 
            side="right"
            align="center"
            sideOffset={5}
            className="bg-popover text-popover-foreground text-sm shadow-md flex items-center gap-2 py-2"
          >
            {content}
          </TooltipContent>
        </Tooltip>
      ) : (
        <span 
          ref={textRef} 
          className={cn("truncate w-full inline-block", className)} 
          style={{ maxWidth: '100%' }}
          {...props}
        >
          {children}
        </span>
      )}
    </div>
  )
}
